import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  EyeIcon, 
  DocumentArrowDownIcon,
  BanknotesIcon,
  ClockIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingStorefrontIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const ShopOwners = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shopOwners, setShopOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [statement, setStatement] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    business_name: '',
    business_address: '',
    business_phone: '',
    town: '',
    district: '',
    credit_limit: 0,
    payment_terms: 30,
    credit_status: 'active'
  });
  const [filters, setFilters] = useState({
    town: '',
    credit_status: '',
    search: ''
  });
  const [towns, setTowns] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0
  });

  useEffect(() => {
    fetchShopOwners();
  }, [pagination.page, pagination.per_page, filters]);

  useEffect(() => {
    if (shopOwners.length > 0) {
      const uniqueTowns = [...new Set(shopOwners.map(owner => owner.profile?.town).filter(Boolean))];
      setTowns(uniqueTowns);
    }
  }, [shopOwners]);

  const fetchShopOwners = async () => {
    try {
      setLoading(true);
      const params = {
        user_type: 'shop_owner',
        page: pagination.page,
        per_page: pagination.per_page,
        ...filters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => 
        params[key] === '' && delete params[key]
      );

      const response = await api.get('/users/shop-owners/', { params });
      console.log("response of GET shop owners in ShopOwners.js = ", response.data);
      setShopOwners(response.data.items || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      toast.error('Failed to fetch shop owners');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShopOwner = async () => {
    try {
      const response = await api.post('/users/shop-owners', formData);
      toast.success('Shop owner created successfully');
      setShowModal(false);
      resetForm();
      fetchShopOwners();
      
      if (response.data.temp_password) {
        toast.success(`Temporary password: ${response.data.temp_password}`, {
          duration: 10000,
          icon: '🔑'
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create shop owner');
    }
  };

  const handleUpdateShopOwner = async () => {
    console.log('Update shop owner with data:', formData);
    try {
      await api.put(`/users/${selectedOwner.id}`, formData);
      toast.success('Shop owner updated successfully');
      setShowModal(false);
      resetForm();
      fetchShopOwners();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update shop owner');
    }
  };

  const handleDeleteShopOwner = async () => {
    try {
      await api.delete(`/users/${selectedOwner.id}`);
      toast.success('Shop owner deactivated successfully');
      setShowDeleteDialog(false);
      fetchShopOwners();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to deactivate shop owner');
    }
  };

  const handleToggleCredit = async (owner, newStatus) => {
    try {
      await api.put(`/users/${owner.id}`, {
        credit_status: newStatus
      });
      toast.success(`Credit ${newStatus === 'active' ? 'enabled' : 'suspended'} successfully`);
      fetchShopOwners();
    } catch (error) {
      toast.error('Failed to update credit status');
    }
  };

  /*
  const handleViewStatement = async (owner) => {
    console.log("owner data am sending in ShopOwners.js = ", owner);
    try {
      setSelectedOwner(owner);
      const response = await api.get(`/payments/statements/shop-owner/${owner.shop_owner_id}`);
      console.log("Responce for Shop Owner using their ID in ShopOwners.js = ", response);
      setStatement(response.data);
      setShowStatementModal(true);
    } catch (error) {
      toast.error('Failed to fetch statement');
    }
  };
*/

const handleViewStatement = async (owner) => {
  console.log("owner data am sending in ShopOwners.js = ", owner);
  try {
    setSelectedOwner(owner);
    const shopOwnerId = owner.shop_owner_id || owner.id;
    const response = await api.get(`/payments/statements/shop-owner/${shopOwnerId}`);
    console.log("Response for Shop Owner using their ID in ShopOwners.js = ", response);
    
    // The data is nested in response.data
    setStatement(response.data);
    setShowStatementModal(true);
  } catch (error) {
    toast.error('Failed to fetch statement');
    console.error('Statement error:', error);
  }
};

  const handleExportStatement = async (format) => {
    try {
      const response = await api.get(`/reports/customer-statement/${selectedOwner.profile.id}`, {
        params: { format },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statement_${selectedOwner.profile?.business_name}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to export statement');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      business_name: '',
      business_address: '',
      business_phone: '',
      town: '',
      district: '',
      credit_limit: 0,
      payment_terms: 30,
      credit_status: 'active'
    });
    setSelectedOwner(null);
  };

  const handleEdit = (owner) => {
    setSelectedOwner(owner);
    setFormData({
      email: owner.email,
      first_name: owner.first_name,
      last_name: owner.last_name,
      phone_number: owner.phone_number || '',
      business_name: owner.profile?.business_name || '',
      business_address: owner.profile?.business_address || '',
      business_phone: owner.profile?.business_phone || '',
      town: owner.profile?.town || '',
      district: owner.profile?.district || '',
      credit_limit: owner.profile?.credit_limit || 0,
      payment_terms: owner.profile?.payment_terms || 30,
      credit_status: owner.profile?.credit_status || 'active'
    });
    setShowModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      town: '',
      credit_status: '',
      search: ''
    });
  };

  const getTotalCredit = () => {
    return shopOwners.reduce((sum, owner) => sum + (owner?.credit_limit || 0), 0);
  };

  const getTotalOutstanding = () => {
    return shopOwners.reduce((sum, owner) => sum + (owner?.current_balance || 0), 0);
  };

  const getActiveCount = () => {
    return shopOwners.filter(owner => owner?.credit_status === 'active').length;
  };

  const columns = [
    {
      header: 'Business',
      accessorKey: 'business_name',
      cell: ({ row }) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
            <BuildingStorefrontIcon className="h-5 w-5 text-primary-600" />
          </div>
          <div className="ml-4">
            <div className="font-medium text-gray-900">{row.original?.business_name}</div>
            <div className="text-xs text-gray-500">{row.original.email}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Contact Person',
      accessorKey: 'full_name',
      cell: ({ row }) => (
        <div>
          <div>{row.original.full_name}</div>
          <div className="text-xs text-gray-500 flex items-center mt-1">
            <PhoneIcon className="h-3 w-3 mr-1" />
            {row.original.profile?.business_phone || row.original.phone_number || '-'}
          </div>
        </div>
      )
    },
    {
      header: 'Location',
      accessorKey: 'profile.town',
      cell: ({ row }) => (
        <div className="flex items-center">
          <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" />
          <div>
            <div>{row.original?.town}</div>
            <div className="text-xs text-gray-500">{row.original?.business_address || '-'}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Credit Limit',
      accessorKey: 'credit_limit',
      cell: ({ getValue }) => (
        <span className="font-medium">ZMW {getValue()?.toLocaleString() || 0}</span>
      )
    },
    {
      header: 'Current Balance',
      accessorKey: 'current_balance',
      cell: ({ getValue }) => {
        const balance = getValue() || 0;
        return (
          <span className={balance > 0 ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
            ZMW {balance.toLocaleString()}
          </span>
        );
      }
    },
    {
      header: 'Credit Status',
      accessorKey: 'credit_status',
      cell: ({ getValue }) => (
        <StatusBadge status={getValue()} />
      )
    },
    {
      header: 'Payment Terms',
      accessorKey: 'payment_terms',
      cell: ({ getValue }) => (
        <div className="flex items-center">
          <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
          <span>{getValue()} days</span>
        </div>
      )
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewStatement(row.original)}
            className="text-blue-600 hover:text-blue-900"
            title="View Statement"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleEdit(row.original)}
            className="text-green-600 hover:text-green-900"
            title="Edit"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          {user?.user_type === 'owner' && (
            <>
              {row.original.profile?.credit_status === 'active' ? (
                <button
                  onClick={() => handleToggleCredit(row.original, 'suspended')}
                  className="text-yellow-600 hover:text-yellow-900"
                  title="Suspend Credit"
                >
                  <BanknotesIcon className="h-5 w-5" />
                </button>
              ) : (
                <button
                  onClick={() => handleToggleCredit(row.original, 'active')}
                  className="text-green-600 hover:text-green-900"
                  title="Enable Credit"
                >
                  <BanknotesIcon className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={() => {
                  setSelectedOwner(row.original);
                  setShowDeleteDialog(true);
                }}
                className="text-red-600 hover:text-red-900"
                title="Deactivate"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Shop Owners</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage shop owners, their credit accounts, and view statements.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex-none space-x-3">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          {user?.user_type !== 'shop_owner' && (
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Shop Owner
          </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Total Shop Owners</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">{pagination.total}</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Active Accounts</dt>
            <dd className="mt-1 text-2xl font-semibold text-green-600">{getActiveCount()}</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Total Credit Limit</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">
              ZMW {getTotalCredit().toLocaleString()}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Outstanding Balance</dt>
            <dd className="mt-1 text-2xl font-semibold text-red-600">
              ZMW {getTotalOutstanding().toLocaleString()}
            </dd>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filter Shop Owners</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Search</label>
              <input
                type="text"
                placeholder="Search by name or business..."
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Town</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.town}
                onChange={(e) => handleFilterChange('town', e.target.value)}
              >
                <option value="">All Towns</option>
                {towns.map(town => (
                  <option key={town} value={town}>{town}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Credit Status</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.credit_status}
                onChange={(e) => handleFilterChange('credit_status', e.target.value)}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchShopOwners}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shop Owners Table */}
      <div className="mt-8">
        <DataTable
          data={shopOwners}
          columns={columns}
          loading={loading}
          pageSize={pagination.per_page}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(per_page) => setPagination(prev => ({ ...prev, per_page, page: 1 }))}
        />
      </div>

      {/* Create/Edit Shop Owner Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={selectedOwner ? 'Edit Shop Owner' : 'Create New Shop Owner'}
        size="lg"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          selectedOwner ? handleUpdateShopOwner() : handleCreateShopOwner();
        }}>
          <div className="space-y-6">
            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    required
                    className="form-input"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="form-label">Last Name </label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="form-label">Email </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    className="form-input pl-10"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    disabled={!selectedOwner}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="form-label">Phone Number</label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    className="form-input pl-10"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Business Information</h3>
              
              <div>
                <label className="form-label">Business Name *</label>
                <div className="relative">
                  <BuildingStorefrontIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    className="form-input pl-10"
                    value={formData.business_name}
                    onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="form-label">Business Address</label>
                <textarea
                  className="form-input"
                  rows="2"
                  value={formData.business_address}
                  onChange={(e) => setFormData({...formData, business_address: e.target.value})}
                />
              </div>

              <div className="mt-4">
                <label className="form-label">Business Phone</label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    className="form-input pl-10"
                    value={formData.business_phone}
                    onChange={(e) => setFormData({...formData, business_phone: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="form-label">Town *</label>
                  <input
                    type="text"
                    required
                    className="form-input"
                    value={formData.town}
                    onChange={(e) => setFormData({...formData, town: e.target.value})}
                  />
                </div>
                <div>
                  <label className="form-label">District</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.district}
                    onChange={(e) => setFormData({...formData, district: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Credit Settings */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Credit Settings</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Credit Limit (ZMW)</label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    className="form-input"
                    value={formData.credit_limit}
                    onChange={(e) => setFormData({...formData, credit_limit: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <label className="form-label">Payment Terms (days)</label>
                  <input
                    type="number"
                    min="1"
                    className="form-input"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({...formData, payment_terms: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              {selectedOwner && (
                <div className="mt-4">
                  <label className="form-label">Credit Status</label>
                  <select
                    className="form-input"
                    value={formData.credit_status}
                    onChange={(e) => setFormData({...formData, credit_status: e.target.value})}
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                {selectedOwner ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Statement Modal */}
<Modal
  isOpen={showStatementModal}
  onClose={() => {
    setShowStatementModal(false);
    setStatement(null);
  }}
  title={`Statement - ${selectedOwner?.business_name || selectedOwner?.profile?.business_name || 'Shop Owner'}`}
  size="xl"
>
  {statement && (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Customer Statement</h3>
          <p className="text-sm text-gray-500">
            Period: {statement.period?.from || 'N/A'} to {statement.period?.to || 'N/A'}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleExportStatement('pdf')}
            className="btn-secondary text-sm"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
            PDF
          </button>
          <button
            onClick={() => handleExportStatement('excel')}
            className="btn-secondary text-sm"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
            Excel
          </button>
        </div>
      </div>

      {/* Customer Info - FIXED: Use statement.shop_owner instead of statement.customer */}
      <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-gray-500">Business Name</p>
          <p className="font-medium">{statement.shop_owner?.business_name || selectedOwner?.business_name || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Contact Person</p>
          <p className="font-medium">{statement.shop_owner?.user?.full_name || selectedOwner?.full_name || selectedOwner?.first_name + ' ' + selectedOwner?.last_name}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Location</p>
          <p className="font-medium">{statement.shop_owner?.town || selectedOwner?.town || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Phone</p>
          <p className="font-medium">{statement.shop_owner?.business_phone || selectedOwner?.business_phone || selectedOwner?.phone_number}</p>
        </div>
      </div>

      {/* Balance Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-green-600">Opening Balance</p>
          <p className="text-2xl font-bold text-green-700">
            ZMW {statement.opening_balance?.toLocaleString() || 0}
          </p>
        </div>
        <div className={`${statement.closing_balance > 0 ? 'bg-red-50' : 'bg-green-50'} p-4 rounded-lg`}>
          <p className={`text-sm ${statement.closing_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
            Closing Balance
          </p>
          <p className={`text-2xl font-bold ${statement.closing_balance > 0 ? 'text-red-700' : 'text-green-700'}`}>
            ZMW {statement.closing_balance?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {statement.entries?.map((entry, index) => (
              <tr key={index} className={index === 0 ? 'bg-gray-50 font-medium' : ''}>
                <td className="px-4 py-3 text-sm text-gray-900">{entry.date || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{entry.description || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{entry.reference || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right">
                  {entry.debit > 0 ? `ZMW ${entry.debit.toLocaleString()}` : '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right">
                  {entry.credit > 0 ? `ZMW ${entry.credit.toLocaleString()}` : '-'}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-right">
                  <span className={entry.balance > 0 ? 'text-red-600' : 'text-green-600'}>
                    ZMW {entry.balance.toLocaleString()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* No entries message */}
      {(!statement.entries || statement.entries.length === 0) && (
        <div className="text-center py-8 text-gray-500">
          No transactions found for this period.
        </div>
      )}

      {/* Footer */}
      <div className="text-center text-sm text-gray-500 pt-4 border-t">
        <p>This is a computer generated statement. No signature required.</p>
      </div>
    </div>
  )}
</Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteShopOwner}
        title="Deactivate Shop Owner"
        message={`Are you sure you want to deactivate ${selectedOwner?.profile?.business_name}? They will no longer be able to make purchases on credit.`}
        confirmText="Deactivate"
        type="danger"
      />
    </div>
  );
};

export default ShopOwners;